import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CourierManagerService } from 'src/app/services/courier-manager/courier-manager.service';
import { CourierManagerModalService } from 'src/app/services/courier-manager-modal/courier-manager-modal.service';
import { CourierCompanyService } from 'src/app/services/courier-company/courier-company.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-bulk-update-courier-manager',
  templateUrl: './bulk-update-courier-manager.component.html',
  styleUrls: ['./bulk-update-courier-manager.component.scss']
})
export class BulkUpdateCourierManagerComponent extends DestructorComponent implements OnInit {

  @Output() bulkUpdated = new EventEmitter<void>();

  bulkForm: FormGroup;
  isLoading: boolean = false;
  selectedItems: any[] = [];

  // Courier Company search properties
  courierCompanySearchTerm: string = '';
  courierCompanySearchResults: any[] = [];
  showCourierCompanyDropdown: boolean = false;
  isCourierCompanySearching: boolean = false;
  private courierCompanySearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _courierManagerService: CourierManagerService,
    private _courierManagerModalService: CourierManagerModalService,
    private _courierCompanyService: CourierCompanyService
  ) {
    super();
    this.initializeForm();
    this.setupCourierCompanySearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.bulkForm = this.fb.group({
      courier_company_id: [''],
      order_date: [''],
      surrender_date: [''],
      complete_date: [''],
      docs_released_date: [''],
      tracking_number: [''],
      status: [''],
      remarks: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_courier_manager_bulk_update');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        this.selectedItems = this._courierManagerModalService.getBulkItems();
      });
      modalElement.addEventListener('hidden.bs.modal', () => {
        this._courierManagerModalService.clearBulkItems();
        this.resetForm();
      });
    }
  }

  private resetForm(): void {
    this.bulkForm.reset();
    this.isLoading = false;
    this.selectedItems = [];
    this.courierCompanySearchTerm = '';
    this.courierCompanySearchResults = [];
    this.showCourierCompanyDropdown = false;
  }

  onSubmit(): void {
    if (this.selectedItems.length === 0) {
      return;
    }

    const ids = this.selectedItems.map(item => item._id || item.id);
    const formValue = this.bulkForm.value;
    const payload: any = { ids };

    Object.keys(formValue).forEach(key => {
      const value = formValue[key];
      if (value !== null && value !== undefined && value !== '') {
        payload[key] = value;
      }
    });

    if (Object.keys(payload).length === 1) {
      Swal.fire({
        title: 'No Changes',
        text: 'Please fill in at least one field to update.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isLoading = true;
    this.subscriptions.push(
      this._courierManagerService.BulkUpdate(payload).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `${ids.length} courier manager(s) updated successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.bulkUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to bulk update courier managers.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error bulk updating courier managers:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while bulk updating courier managers.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_courier_manager_bulk_update');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Courier Company search methods
  private setupCourierCompanySearchDebounce(): void {
    this.courierCompanySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCourierCompanies(searchTerm);
    });
  }

  onCourierCompanySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.courierCompanySearchTerm = target.value;
    this.bulkForm.patchValue({ courier_company_id: '' });

    if (this.courierCompanySearchTerm.length >= 2) {
      this.courierCompanySearchSubject.next(this.courierCompanySearchTerm);
      this.showCourierCompanyDropdown = true;
    } else {
      this.showCourierCompanyDropdown = false;
      this.courierCompanySearchResults = [];
    }
  }

  onCourierCompanyInputFocus(): void {
    if (this.courierCompanySearchResults.length > 0) {
      this.showCourierCompanyDropdown = true;
    }
  }

  onCourierCompanyInputBlur(): void {
    setTimeout(() => {
      this.showCourierCompanyDropdown = false;
    }, 200);
  }

  private searchCourierCompanies(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.courierCompanySearchResults = [];
      this.showCourierCompanyDropdown = false;
      return;
    }

    this.isCourierCompanySearching = true;
    this.subscriptions.push(
      this._courierCompanyService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isCourierCompanySearching = false;
          if (response.status && response.data) {
            this.courierCompanySearchResults = response.data.data || response.data || [];
            this.showCourierCompanyDropdown = this.courierCompanySearchResults.length > 0;
          } else {
            this.courierCompanySearchResults = [];
            this.showCourierCompanyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCourierCompanySearching = false;
          console.error('Error searching courier companies:', error);
          this.courierCompanySearchResults = [];
          this.showCourierCompanyDropdown = false;
        }
      })
    );
  }

  selectCourierCompany(courierCompany: any): void {
    this.courierCompanySearchTerm = courierCompany.company_name || courierCompany.name || 'Unknown Company';
    this.bulkForm.patchValue({ courier_company_id: courierCompany._id || courierCompany.id });
    this.showCourierCompanyDropdown = false;
  }
}
